import React, { useEffect, useState, useCallback } from "react";
import { Gift, Package, ShieldCheck, Truck, Check, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const emptyShip = { name: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "US" };
const emptyW9 = { legal_name: "", tax_classification: "individual", address: "", tin: "" };
const BE = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  pending_claim: "Unclaimed", ready_to_fulfill: "Processing",
  shipped: "Shipped", redeemed: "Ready",
};

export const MyPrizes = () => {
  const [data, setData] = useState({ prizes: [] });
  const [active, setActive] = useState(null);       // award being claimed
  const [check, setCheck] = useState(null);
  const [ship, setShip] = useState(emptyShip);
  const [w9, setW9] = useState(emptyW9);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/prizes").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const openClaim = async (p) => {
    setActive(p); setCheck(null); setShip(emptyShip); setW9(emptyW9);
    try { const { data: r } = await api.get(`/prizes/${p.award_id}/claim-check`); setCheck(r); }
    catch (e) { toast.error(apiError(e.response?.data?.detail?.message || e.response?.data?.detail)); }
  };

  const startKyc = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/kyc/session", { origin_url: window.location.origin });
      if (r.already_verified) { toast.success("Already verified"); openClaim(active); setBusy(false); return; }
      window.location.href = r.url;
    } catch (e) { toast.error(apiError(e.response?.data?.detail?.message || e.response?.data?.detail)); setBusy(false); }
  };

  const submitClaim = async () => {
    const body = {};
    if (check?.needs_shipping) {
      if (!ship.name || !ship.line1 || !ship.city || !ship.state || !ship.postal_code)
        return toast.error("Complete your shipping address.");
      body.shipping = ship;
    }
    if (check?.w9_required) {
      if (!w9.legal_name || !w9.address || !w9.tin) return toast.error("Complete the W-9 details for tax reporting.");
      body.w9 = w9;
    }
    setBusy(true);
    try {
      await api.post(`/prizes/${active.award_id}/claim`, body);
      toast.success("Prize claimed! We'll fulfill it shortly.");
      setActive(null); load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "kyc_required") { toast.message("Verify your identity to claim."); return startKyc(); }
      toast.error(apiError(d?.message || d));
    } finally { setBusy(false); }
  };

  if (!data.prizes || data.prizes.length === 0) return null;

  return (
    <div className="bg-race-surface border border-race-line p-5 mb-6" data-testid="my-prizes">
      <h2 className="font-heading text-2xl tracking-tight mb-4 flex items-center gap-2">
        <Gift size={20} className="text-race-red" /> My Prizes
      </h2>
      <div className="space-y-3">
        {data.prizes.map((p) => (
          <div key={p.award_id} data-testid={`prize-${p.award_id}`} className="bg-race-bg border border-race-line rounded-sm p-4">
            {p.image_url && (
              <img src={`${BE}${p.image_url}`} alt={p.description} data-testid={`prize-image-${p.award_id}`}
                className="w-full max-h-44 object-contain rounded-sm border border-race-line mb-3 bg-race-surface" />
            )}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                {p.prize_type === "gift_card" ? <Gift size={16} className="text-race-volt" /> : <Package size={16} className="text-race-volt" />}
                <span className="font-medium text-race-txt">{p.description}</span>
                <span className="text-xs text-race-sub">${(p.fmv_usd || 0).toLocaleString()}</span>
              </div>
              <span className="text-[10px] uppercase font-mono border border-race-line rounded-full px-2 py-0.5 text-race-sub" data-testid={`prize-status-${p.award_id}`}>
                {STATUS_LABEL[p.status] || p.status}
              </span>
            </div>
            <div className="text-xs text-race-sub mt-1">Won in {p.contest_name}</div>

            {p.status === "pending_claim" && (
              <button onClick={() => openClaim(p)} data-testid={`prize-claim-${p.award_id}`}
                className="mt-3 bg-race-red hover:bg-race-redhover text-race-bg font-mono text-xs uppercase tracking-wide px-4 py-2 rounded-sm transition-colors">
                Claim prize
              </button>
            )}
            {p.status === "ready_to_fulfill" && <div className="text-xs text-race-volt mt-2">Claimed - being prepared for delivery.</div>}
            {p.status === "shipped" && (
              <div className="text-xs text-green-400 mt-2 flex items-center gap-1" data-testid={`prize-shipped-${p.award_id}`}>
                <Truck size={13} /> Shipped{p.tracking_number ? ` · ${p.carrier || "Tracking"}: ${p.tracking_number}` : ""}
              </div>
            )}
            {p.status === "redeemed" && p.gift_card_code && (
              <div className="mt-2 flex items-center gap-2 bg-race-surface border border-green-400/40 rounded-sm px-3 py-2" data-testid={`prize-code-${p.award_id}`}>
                <span className="text-xs text-race-sub">Code:</span>
                <span className="font-mono text-green-400 text-sm break-all">{p.gift_card_code}</span>
                <button onClick={() => { navigator.clipboard.writeText(p.gift_card_code); toast.success("Copied"); }} className="text-race-sub hover:text-race-txt ml-auto"><Copy size={13} /></button>
              </div>
            )}

            {/* Claim panel */}
            {active?.award_id === p.award_id && (
              <div className="mt-3 border-t border-race-line pt-3" data-testid={`prize-claim-panel-${p.award_id}`}>
                {!check ? <div className="text-sm text-race-sub">Loading…</div> : !check.kyc_verified ? (
                  <div>
                    <p className="text-sm text-race-txt mb-2 flex items-center gap-1.5"><ShieldCheck size={15} className="text-race-volt" /> A one-time identity check is required to receive any prize.</p>
                    <button onClick={startKyc} disabled={busy} data-testid={`prize-verify-${p.award_id}`}
                      className="bg-race-volt text-race-bg font-heading text-lg tracking-wide px-5 py-2.5 rounded-sm hover:opacity-90 disabled:opacity-50 transition-opacity">
                      {busy ? "Opening…" : "Verify identity"}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {check.needs_shipping && (
                      <div>
                        <div className="overline text-race-sub mb-1">Shipping address</div>
                        <div className="grid grid-cols-2 gap-2">
                          <input className={IN} placeholder="Full name" value={ship.name} onChange={(e) => setShip({ ...ship, name: e.target.value })} data-testid={`ship-name-${p.award_id}`} />
                          <input className={IN} placeholder="Address line 1" value={ship.line1} onChange={(e) => setShip({ ...ship, line1: e.target.value })} data-testid={`ship-line1-${p.award_id}`} />
                          <input className={IN} placeholder="Address line 2 (opt)" value={ship.line2} onChange={(e) => setShip({ ...ship, line2: e.target.value })} />
                          <input className={IN} placeholder="City" value={ship.city} onChange={(e) => setShip({ ...ship, city: e.target.value })} data-testid={`ship-city-${p.award_id}`} />
                          <input className={IN} placeholder="State" value={ship.state} onChange={(e) => setShip({ ...ship, state: e.target.value })} data-testid={`ship-state-${p.award_id}`} />
                          <input className={IN} placeholder="ZIP / Postal" value={ship.postal_code} onChange={(e) => setShip({ ...ship, postal_code: e.target.value })} data-testid={`ship-zip-${p.award_id}`} />
                        </div>
                      </div>
                    )}
                    {check.w9_required && (
                      <div className="bg-race-bg border border-sky-400/30 rounded-sm p-3" data-testid={`w9-panel-${p.award_id}`}>
                        <div className="overline text-sky-400 mb-1">Tax info (W-9) - required over ${check.w9_threshold_usd?.toLocaleString()}/yr</div>
                        <p className="text-[11px] text-race-sub mb-2">Your prize winnings reach the IRS 1099 threshold this year. We store only the last 4 digits of your TIN.</p>
                        <div className="grid grid-cols-2 gap-2">
                          <input className={IN} placeholder="Legal name" value={w9.legal_name} onChange={(e) => setW9({ ...w9, legal_name: e.target.value })} data-testid={`w9-name-${p.award_id}`} />
                          <select className={IN} value={w9.tax_classification} onChange={(e) => setW9({ ...w9, tax_classification: e.target.value })} data-testid={`w9-class-${p.award_id}`}>
                            <option value="individual">Individual</option>
                            <option value="sole_prop">Sole proprietor</option>
                            <option value="llc">LLC</option>
                            <option value="corp">Corporation</option>
                            <option value="other">Other</option>
                          </select>
                          <input className={IN} placeholder="Address" value={w9.address} onChange={(e) => setW9({ ...w9, address: e.target.value })} data-testid={`w9-address-${p.award_id}`} />
                          <input className={IN} placeholder="SSN / EIN (TIN)" value={w9.tin} onChange={(e) => setW9({ ...w9, tin: e.target.value })} data-testid={`w9-tin-${p.award_id}`} />
                        </div>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button onClick={submitClaim} disabled={busy} data-testid={`prize-claim-submit-${p.award_id}`}
                        className="bg-race-red hover:bg-race-redhover text-race-bg font-mono text-xs uppercase tracking-wide px-5 py-2.5 rounded-sm transition-colors disabled:opacity-50">
                        <Check size={14} className="inline mr-1" />{busy ? "Claiming…" : "Confirm claim"}
                      </button>
                      <button onClick={() => setActive(null)} className="border border-race-line text-race-sub hover:text-race-txt px-4 py-2.5 rounded-sm text-xs font-mono">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const IN = "w-full bg-race-surface border border-race-line focus:border-race-red outline-none px-3 py-2 rounded-sm text-race-txt text-sm";
export default MyPrizes;
